/**
 * useConnectedWork — everything attached to a matter, from ONE endpoint.
 *
 * `GET /matters/{matter_uuid}/connected` returns the research sessions,
 * drafts, insights, reviews and tables linked to a matter, plus the counts and
 * the vocabulary of types it will accept, in a single response. It replaces
 * three separate calls the Connected pane used to make and stitch together
 * itself:
 *
 *   - `research/chat-groups`, paged and then narrowed by `matter_uuid` on the
 *     client because the server filter wanted a numeric id the details
 *     payload did not always carry
 *   - `GET /drafts/list/{uuid}/merged`, which returns the whole draft-type
 *     CATALOGUE — every row at `status: 'not_generated'` had to be filtered
 *     out here or the group listed two dozen documents that do not exist
 *   - `GET /insights/list/{uuid}`
 *
 * Beyond being one call instead of three, `/connected` is also the CORRECT
 * one: the old draft query read the legacy `matter_uuid` column, so a draft
 * linked through an attachment never appeared. `/connected` uses the
 * attachment-aware query and returns it.
 *
 * It lives in a hook rather than inside the pane because the counts are
 * needed whether or not the pane is open — the rail badge shows them while
 * the pane is closed, and a pane that is not mounted cannot report anything.
 *
 * The WRITE half lives here too (`connectResourceToMatter` /
 * `disconnectResourceFromMatter`), because "what is connected" and "connect
 * this" are the same endpoint and the same vocabulary; splitting them into an
 * action file is how the two drift.
 */

import { useCallback, useEffect, useState } from 'react';
import apis from '../../../utils/apis';

export interface ConnectedResearchSession {
  uuid: string;
  chat_group_uuid?: string;
  title?: string;
  status?: string;
  updated_at?: string;
  last_message_at?: string;
}

export interface ConnectedDraft {
  uuid: string;
  document_uuid?: string;
  document_type_id?: number | string;
  title?: string;
  category?: string;
  status?: string;
  updated_at?: string;
}

export interface ConnectedInsight {
  uuid: string;
  title?: string;
  type?: string;
  status?: string;
  updated_at?: string;
}

export interface ConnectedReview {
  uuid: string;
  review_uuid?: string;
  title?: string;
  status?: string;
  document_count?: number;
  findings_count?: number;
  critical_count?: number;
  overall_risk_score?: number;
  finalized_at?: string;
  updated_at?: string;
}

export interface ConnectedTable {
  uuid: string;
  table_uuid?: string;
  title?: string;
  mode?: string;
  status?: string;
  document_count?: number;
  column_count?: number;
  cells_completed?: number;
  cells_total?: number;
  updated_at?: string;
}

export interface ConnectedCounts {
  research_sessions: number;
  drafts: number;
  insights: number;
  reviews: number;
  tables: number;
  total: number;
}

/**
 * The vocabulary `POST /matters/{uuid}/connected` accepts, and the one the
 * DELETE path is addressed by. Kept as a const so the picker, the pane and
 * the request builder cannot drift from each other.
 */
export const CONNECTABLE_TYPES = [
  'research_session',
  'draft',
  'insight',
  'review',
  'table',
] as const;

export type ConnectableType = typeof CONNECTABLE_TYPES[number];

export interface ConnectedWork {
  research_sessions: ConnectedResearchSession[];
  drafts: ConnectedDraft[];
  insights: ConnectedInsight[];
  reviews: ConnectedReview[];
  tables: ConnectedTable[];
  /**
   * What the SERVER says it will accept. An older backend omits it, in which
   * case the client falls back to the full list — the request is then still
   * correct wherever the endpoint understands it, and rejected with a 400
   * where it does not, rather than the whole picker disappearing.
   */
  connectable_types: ConnectableType[];
  counts: ConnectedCounts;
}

const EMPTY_COUNTS: ConnectedCounts = {
  research_sessions: 0,
  drafts: 0,
  insights: 0,
  reviews: 0,
  tables: 0,
  total: 0,
};

export const EMPTY_CONNECTED: ConnectedWork = {
  research_sessions: [],
  drafts: [],
  insights: [],
  reviews: [],
  tables: [],
  connectable_types: [...CONNECTABLE_TYPES],
  counts: EMPTY_COUNTS,
};

const asArray = <T,>(value: any): T[] => (Array.isArray(value) ? value : []);

/**
 * Normalise the response.
 *
 * `counts` is taken from the payload when it is there — it is the server's
 * own answer and may legitimately differ from the returned page — and
 * derived from the arrays when it is not, so a caller never has to handle
 * one of the two being absent.
 */
export function toConnectedWork(payload: any): ConnectedWork {
  const data = payload?.data ?? payload ?? {};
  const research = asArray<ConnectedResearchSession>(data.research_sessions);
  const drafts = asArray<ConnectedDraft>(data.drafts);
  const insights = asArray<ConnectedInsight>(data.insights);
  const reviews = asArray<ConnectedReview>(data.reviews);
  const tables = asArray<ConnectedTable>(data.tables);
  const counts = data.counts || {};
  const offered = asArray<string>(data.connectable_types)
    .filter((type): type is ConnectableType =>
      (CONNECTABLE_TYPES as readonly string[]).includes(type));
  return {
    research_sessions: research,
    drafts,
    insights,
    reviews,
    tables,
    // An older backend says nothing about what it accepts — assume the full
    // vocabulary rather than offering the user nothing to connect.
    connectable_types: offered.length ? offered : [...CONNECTABLE_TYPES],
    counts: {
      research_sessions: Number(counts.research_sessions ?? research.length),
      drafts: Number(counts.drafts ?? drafts.length),
      insights: Number(counts.insights ?? insights.length),
      reviews: Number(counts.reviews ?? reviews.length),
      tables: Number(counts.tables ?? tables.length),
      total: Number(
        counts.total
          ?? research.length + drafts.length + insights.length
             + reviews.length + tables.length
      ),
    },
  };
}

/**
 * Connect one tool resource to a matter — `POST /matters/{uuid}/connected`.
 *
 * Resolves to true on success and false on any rejection, so a picker can
 * report per-row rather than tearing down on the first 403 (a licensed-section
 * gate on ONE tool must not stop the others being linked).
 */
export async function connectResourceToMatter(
  matterUuid: string,
  resourceType: ConnectableType,
  resourceUuid: string
): Promise<boolean> {
  try {
    await apis.post(`/matters/${matterUuid}/connected`, {
      resource_type: resourceType,
      resource_uuid: resourceUuid,
    });
    return true;
  } catch {
    return false;
  }
}

/** Disconnect one resource — `DELETE /matters/{uuid}/connected/{type}/{uuid}`. */
export async function disconnectResourceFromMatter(
  matterUuid: string,
  resourceType: ConnectableType,
  resourceUuid: string
): Promise<boolean> {
  try {
    await apis.delete(`/matters/${matterUuid}/connected/${resourceType}/${resourceUuid}`);
    return true;
  } catch {
    return false;
  }
}

export interface UseConnectedWorkResult {
  work: ConnectedWork;
  loading: boolean;
  refresh: () => void;
}

export function useConnectedWork(matterUuid: string): UseConnectedWorkResult {
  const [work, setWork] = useState<ConnectedWork>(EMPTY_CONNECTED);
  const [loading, setLoading] = useState(false);
  const [nonce, setNonce] = useState(0);

  const refresh = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    if (!matterUuid) {
      setWork(EMPTY_CONNECTED);
      return;
    }
    let cancelled = false;
    setLoading(true);
    apis
      .get(`/matters/${matterUuid}/connected`)
      .then((response) => {
        if (!cancelled) setWork(toConnectedWork(response?.data));
      })
      .catch(() => {
        if (!cancelled) setWork(EMPTY_CONNECTED);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [matterUuid, nonce]);

  return { work, loading, refresh };
}

export default useConnectedWork;
